<style scoped>
.btn_shadow {
  background: white 0% 0% no-repeat padding-box;
  box-shadow: 4px 4px 10px #838383, -4px -4px 10px #ffffff !important;
}

.headline {
  background-color: #1b2643;
  width: 100%;
  height: 60px;
}

.headline p {
  color: #ffffff;
  font-size: 14px;
}

.content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}
</style>

<template>
  <div>
    <div class="pa-3 headline">
      <p>{{ $t('newPackage.header') }}</p>
    </div>
    <div class="content d-flex flex-column pa-10" style="gap: 10px">
        <p class="text-h6">{{ $t('newPackage.subHeader') }}</p>

        <v-text-field
          ref="packageCodeFieldRef"
          v-model="packageCode"
          outlined
          persistent-placeholder
          :label="$t('newPackage.packageCode')"
          :rules="[rules.required, rules.validateHalfWidthAlphanumeric]"
          :error-messages="duplicatedPackageCodeMessage"
          @blur="validateDuplicatedCode(packageCode)"
        ></v-text-field>

        <v-text-field
          v-model="packageName"
          outlined
          persistent-placeholder
          :label="$t('newPackage.packageName')"
          :rules="[rules.required]"
        ></v-text-field>

        <p class="pt-5 mb-0">
          <span class="text-h6 mr-2">{{ $t('newPackage.coupon.header') }}</span>
          <span class="text-body-2">{{ $t('newPackage.coupon.text') }}</span>
        </p>

        <v-tabs v-model="couponInputMethod" color="#1b2643">
          <v-tab tab-value="csv">{{ $t('newPackage.coupon.tabs.csv') }}</v-tab>
          <v-tab tab-value="seats">
            {{ $t('newPackage.coupon.tabs.seats') }}
          </v-tab>
        </v-tabs>
        <v-divider class="mt-n3"></v-divider>

        <p v-if="activeCoupons.length > 0" class="text-body-2 mb-0">
          <v-icon small>mdi-ticket-outline</v-icon>
          {{
            $tc('newPackage.coupon.seatsSummary', activeCoupons.length, {
              count: activeCoupons.length,
            })
          }}
        </p>

        <v-tabs-items v-model="couponInputMethod">
          <v-tab-item value="csv" class="pt-4">
            <p
              class="text-body-2"
              v-html="$t('newPackage.coupon.csv.text')"
            ></p>

            <v-row>
              <v-col cols="7">
                <v-file-input
                  v-model="file"
                  accept="text/csv"
                  persistent-placeholder
                  outlined
                  :label="$t('newPackage.coupon.couponFormat')"
                  :error-messages="CSVInputErrorMessage"
                  :rules="[rules.required]"
                  @change="validateCSVInputFile"
                ></v-file-input>
              </v-col>

              <v-col cols="5" class="mt-2">
                <v-btn class="btn_shadow_half" @click="getFileSample">
                  <v-icon small> mdi-download </v-icon>
                  {{ $t('newPackage.coupon.buttonText') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item value="seats" class="pt-4">
            <v-text-field
              v-model.number="seatCount"
              type="number"
              :min="1"
              :max="maxSeats"
              outlined
              dense
              persistent-placeholder
              style="max-width: 200px"
              :label="$t('newPackage.coupon.seats.label')"
              :rules="[rules.required, rules.minOne, rules.maxSeats]"
              @wheel="blurOnWheel"
            ></v-text-field>

            <p class="text-body-2">{{ $t('newPackage.coupon.seats.text') }}</p>

            <v-expansion-panels flat>
              <v-expansion-panel :disabled="generatedCoupons.length === 0">
                <v-expansion-panel-header class="px-0" hide-actions>
                  <template #default="{ open }">
                    <span class="d-flex align-center font-weight-medium">
                      {{ $t('newPackage.coupon.seats.preview') }}
                      <v-icon class="ml-1">
                        {{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </span>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table
                    :headers="couponTableHeaders"
                    :items="generatedCoupons"
                    :items-per-page="10"
                    :footer-props="{
                      'items-per-page-options': [10, 100, -1],
                    }"
                    item-key="couponCode"
                    dense
                  ></v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-tab-item>
        </v-tabs-items>

        <p class="text-h6 pt-5 mb-0">{{ $t('newPackage.viewingPeriod.text') }}</p>

        <PackageViewingPeriod
          :startDate="startViewingDate"
          :startTime="startViewingTime"
          :endDate="endViewingDate"
          :endTime="endViewingTime"
          :errorMessage="viewingPeriodErrorMessage"
          @update:startDate="startViewingDate = $event"
          @update:startTime="startViewingTime = $event"
          @update:endDate="endViewingDate = $event"
          @update:endTime="endViewingTime = $event"
          @validate="validateViewingPeriod"
        />

        <div class="pt-5">
          <p class="text-h6">{{ $t('newPackage.campaign.text') }}</p>

          <ModifiableCampaignTable
            :campaigns="newCampaign"
            :availableScenarios="scenarios"
            :rules="rules"
            :getScenarioDisplayName="scenarioDisplayName"
            :startViewingDate="startViewingDate"
            :startViewingTime="startViewingTime"
            :endViewingDate="endViewingDate"
            :endViewingTime="endViewingTime"
            @update-campaign="onUpdateCampaignField"
            @move-up="moveUp"
            @move-down="moveDown"
            @delete-campaign="deleteCampaign"
            @create-campaign="createNewCampaign"
            @validate-period="validateViewingPeriod"
            @same-as-viewing-period-change="onSameAsViewingPeriodChange"
          />
        </div>

        <div class="d-flex justify-center pa-8">
          <v-dialog width="600" persistent>
            <template #activator="{ on, attrs }">
              <v-btn
                color="#1b2643"
                class="white--text"
                v-bind="attrs"
                v-on="on"
                :disabled="isInputError"
                @click="confirmDialogKey++"
              >
                {{ $t('newPackage.campaign.confirm') }}
              </v-btn>
            </template>
            <template #default="dialog">
              <ConfirmCreateCampaignTable
                :key="confirmDialogKey"
                :scenarios="scenarios"
                :agentGroups="agentGroups"
                :campaigns="newCampaign"
                :coupons="activeCoupons"
                :packageCode="packageCode"
                :packageName="packageName"
                :startViewingDate="startViewingDate"
                :startViewingTime="startViewingTime"
                :endViewingDate="endViewingDate"
                :endViewingTime="endViewingTime"
                @cancel="dialog.value = false"
                @resetValues="resetValues"
                @duplicateCode="
                  handleDuplicatePackageCode()
                  dialog.value = false
                "
              />
            </template>
          </v-dialog>
        </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import dayjs from "dayjs";
import Papa from "papaparse";

import { agentGroupService } from "@/api/agentGroupService";
import { campaignService } from "@/api/campaignService";
import { packageService } from "@/api/packageService";
import ModifiableCampaignTable from "@/components/Dashboard/EditPackage/ModifiableCampaignTable.vue";
import ConfirmCreateCampaignTable from "@/components/Dashboard/PageViewItems/ConfirmCreateCampaignTable.vue";
import PackageViewingPeriod from "@/components/Dashboard/PageViewItems/PackageViewingPeriod.vue";
import { createIsoUtcDateTime } from "@/components/utils/dateUtils";
import type { AgentGroup } from "@/resources/agentGroup";
import type { Campaign } from "@/resources/campaign";
import type { EditableCoupon } from "@/resources/coupon";
import type { NewCampaign } from "@/resources/newCampaign";
import type { Package } from "@/resources/package";
import type { ScenarioWithId } from "@/resources/scenario";
import { fetchScenarios, getScenarioDisplayName } from "@/utils/scenarioUtils";
import { generateDefaultCoupons, MAX_SEATS } from "@/utils/seatUtils";

export default defineComponent({
  name: "PackageNew",
  components: {
    ConfirmCreateCampaignTable,
    ModifiableCampaignTable,
    PackageViewingPeriod,
  },

  data() {
    return {
      allPackages: [] as Package[],
      allCampaigns: [] as Partial<Campaign>[],
      file: null as File | null,
      confirmDialogKey: 0,
      newCampaign: [
        {
          name: "",
          scenarioDocId: "",
          isSameAsViewingPeriod: true,
          startInterviewDate: null,
          startInterviewTime: "00:00",
          endInterviewDate: null,
          endInterviewTime: "23:59",
          errorMessage: "",
          limits: 1,
          limitMenuOpen: false,
          startAt: "",
          endAt: "",
        },
      ] as NewCampaign[],
      csvCoupons: [] as EditableCoupon[],
      couponInputMethod: "csv" as "csv" | "seats",
      seatCount: 1 as number | string | null,
      maxSeats: MAX_SEATS,
      scenarios: [] as ScenarioWithId[],
      agentGroups: [] as AgentGroup[],
      packageCode: "" as string,
      packageName: "" as string,

      duplicatedPackageCodeMessage: "" as string,
      CSVInputErrorMessage: "" as string | null,
      viewingPeriodErrorMessage: "" as string,

      startViewingDate: dayjs().format("YYYY-MM-DD") as string | null,
      startViewingTime: "00:00" as string,
      endViewingDate: dayjs().add(1, "year").format("YYYY-MM-DD") as
        | string
        | null,
      endViewingTime: "23:59" as string,
    };
  },

  async mounted() {
    this.getAllPackages();
    this.getAllCampaigns();
    this.getScenarios();
    this.newCampaign.forEach((c) => {
      if (c.isSameAsViewingPeriod) {
        c.startInterviewDate = this.startViewingDate;
        c.startInterviewTime = this.startViewingTime;
        c.endInterviewDate = this.endViewingDate;
        c.endInterviewTime = this.endViewingTime;
      }
    });
  },

  computed: {
    rules() {
      return {
        required: (value: string): boolean | string =>
          !!value || (this.$t("newPackage.validation.required") as string),
        minOne: (value: number | string): boolean | string =>
          Number(value) >= 1 ||
          (this.$t("newPackage.validation.minOne") as string),
        maxSeats: (value: number | string): boolean | string =>
          (Number.isInteger(Number(value)) && Number(value) <= MAX_SEATS) ||
          (this.$t("newPackage.validation.maxSeats", {
            max: MAX_SEATS,
          }) as string),
        validateHalfWidthAlphanumeric: (value: string): boolean | string =>
          /^[a-zA-Z0-9-_]*$/.test(value) ||
          (this.$t(
            "newPackage.validation.packageCode.halfWithAlphanumbericError",
          ) as string),
      };
    },

    couponTableHeaders(): { text: string; value: keyof EditableCoupon }[] {
      return [
        {
          text: this.$t(
            "newPackage.confirmTable.campaigns.coupon.body[0]",
          ) as string,
          value: "couponCode",
        },
        {
          text: this.$t(
            "newPackage.confirmTable.campaigns.coupon.body[1]",
          ) as string,
          value: "password",
        },
      ];
    },

    generatedCoupons(): EditableCoupon[] {
      if (this.seatCount === null || this.seatCount === "") return [];
      const count = Number(this.seatCount);
      if (count > MAX_SEATS) return [];
      return generateDefaultCoupons(count);
    },

    activeCoupons(): EditableCoupon[] {
      if (this.couponInputMethod === "seats") return this.generatedCoupons;
      if (this.file === null || this.CSVInputErrorMessage !== "") return [];
      return this.csvCoupons;
    },

    isCouponInputError(): boolean {
      if (this.couponInputMethod === "seats") {
        return this.generatedCoupons.length === 0;
      }
      return this.activeCoupons.length === 0;
    },

    isInputError() {
      let disabled = false;

      if (this.packageCode === "") disabled = true;
      if (this.packageCode && !/^[a-zA-Z0-9-_]*$/.test(this.packageCode))
        disabled = true;
      if (this.packageName === "") disabled = true;
      if (this.newCampaign.length === 0) disabled = true;

      if (
        !this.startViewingDate ||
        !this.startViewingTime ||
        !this.endViewingDate ||
        !this.endViewingTime
      ) {
        disabled = true;
      }

      if (
        this.newCampaign.some(
          (campaign) =>
            campaign.name === "" ||
            (!campaign.isDynamic && !campaign.scenarioDocId) ||
            (campaign.limits !== null && Number(campaign.limits) < 1) ||
            (!campaign.isSameAsViewingPeriod &&
              (!campaign.startInterviewDate ||
                !campaign.startInterviewTime ||
                !campaign.endInterviewDate ||
                !campaign.endInterviewTime)),
        )
      ) {
        disabled = true;
      }

      if (this.duplicatedPackageCodeMessage !== "") disabled = true;
      if (this.viewingPeriodErrorMessage !== "") disabled = true;
      if (this.newCampaign.some((campaign) => campaign.errorMessage !== ""))
        disabled = true;
      if (this.isCouponInputError) disabled = true;

      return disabled;
    },
  },

  watch: {
    startViewingDate(newVal) {
      this.newCampaign.forEach((c) => {
        if (c.isSameAsViewingPeriod) c.startInterviewDate = newVal;
      });
    },
    startViewingTime(newVal) {
      this.newCampaign.forEach((c) => {
        if (c.isSameAsViewingPeriod) c.startInterviewTime = newVal;
      });
    },
    endViewingDate(newVal) {
      this.newCampaign.forEach((c) => {
        if (c.isSameAsViewingPeriod) c.endInterviewDate = newVal;
      });
    },
    endViewingTime(newVal) {
      this.newCampaign.forEach((c) => {
        if (c.isSameAsViewingPeriod) c.endInterviewTime = newVal;
      });
    },
  },

  methods: {
    async getAllPackages() {
      const packages = await packageService.list();
      this.allPackages = packages;
    },

    async getAllCampaigns() {
      const campaigns = await campaignService.list();
      this.allCampaigns = campaigns.map((campaign) => ({
        campaignId: campaign.id,
        campaignCode: campaign.campaignCode,
        campaignName: campaign.name,
      }));
    },

    async fetchAgentGroups(): Promise<AgentGroup[]> {
      return await agentGroupService.list();
    },

    async getCustomerScenarios(): Promise<ScenarioWithId[]> {
      const scenarios = await fetchScenarios();
      return scenarios.filter(
        (scenario) => scenario !== null,
      ) as ScenarioWithId[];
    },

    async getScenarios() {
      const [scenarios, agentGroups] = await Promise.all([
        this.getCustomerScenarios(),
        this.fetchAgentGroups(),
      ]);
      this.agentGroups = agentGroups;
      const agentGroupsMap = Object.fromEntries(
        agentGroups.map((agentGroup) => [agentGroup.id, agentGroup]),
      );
      this.scenarios = scenarios.filter(
        (scenario) => scenario.agentGroupId in agentGroupsMap,
      );
    },

    scenarioDisplayName(scenario: ScenarioWithId): string {
      const item = getScenarioDisplayName(scenario, this.agentGroups);
      return item.scenarioDisplayName;
    },

    getFileSample() {
      const csvContent = [
        ["ticketId0", "password0"],
        ["ticketId1", "password1"],
        ["ticketId2", "password2"],
        ["ticketId3", "password3"],
        ["ticketId4", "password4"],
      ];
      const csvString = Papa.unparse(csvContent);
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "ticket-sample.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    blurOnWheel(event: WheelEvent) {
      (event.target as HTMLInputElement).blur();
    },

    handleDuplicatePackageCode() {
      this.duplicatedPackageCodeMessage = this.$t(
        "newPackage.validation.packageCode.duplicatedCodeError",
      ) as string;
      this.$nextTick(() => {
        const fieldRef = this.$refs.packageCodeFieldRef as
          | { $el: HTMLElement }
          | undefined;
        fieldRef?.$el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    },

    async validateDuplicatedCode(value: string) {
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.toLowerCase();
      const isDuplicatedPackageCode = this.allPackages.some(
        (pkg) => pkg.code.trim().toLowerCase() === normalizedValue,
      );
      const isDuplicatedCampaignCode = this.allCampaigns.some(
        (campaign) => campaign.campaignCode === value,
      );
      if (isDuplicatedCampaignCode || isDuplicatedPackageCode) {
        this.duplicatedPackageCodeMessage = this.$t(
          "newPackage.validation.packageCode.duplicatedCodeError",
        ) as string;
      } else {
        this.duplicatedPackageCodeMessage = "";
      }
    },

    validateCSVInputFile(file: File) {
      this.CSVInputErrorMessage = "";
      this.csvCoupons = [];
      if (!file) return;
      Papa.parse(file, {
        complete: (result: { data: string[][] }) => {
          const data = result.data;
          if (data.length === 0) {
            this.CSVInputErrorMessage = this.$t(
              "newPackage.validation.CSVFile.incompleteColumnError",
            ) as string;
            return;
          }
          const codes = new Set();
          for (let i = 0; i < data.length; i++) {
            const row = data[i];
            if (row.length === 0) continue;
            const [couponCode, password] = row;
            if (!couponCode || !password) {
              this.CSVInputErrorMessage = this.$t(
                "newPackage.validation.CSVFile.blankCouponError",
              ) as string;
              break;
            }
            if (row.length !== 2) {
              this.CSVInputErrorMessage = this.$t(
                "newPackage.validation.CSVFile.incompleteColumnError",
              ) as string;
              break;
            }
            if (codes.has(couponCode)) {
              this.CSVInputErrorMessage = this.$t(
                "newPackage.validation.CSVFile.duplicatedCouponsError",
              ) as string;
              break;
            } else {
              codes.add(couponCode);
            }
            const validPattern = /^[A-Za-z0-9\-_]+$/;
            if (
              !validPattern.test(couponCode) ||
              !validPattern.test(password)
            ) {
              this.CSVInputErrorMessage = this.$t(
                "newPackage.validation.CSVFile.halfWithAlphanumbericError",
              ) as string;
              break;
            }
            this.csvCoupons = data
              .filter((row) => row.length >= 2)
              .map((coupon) => ({
                couponCode: coupon[0],
                password: coupon[1],
              }));
            this.CSVInputErrorMessage = "";
          }
        },
      });
    },

    validateViewingPeriod() {
      this.viewingPeriodErrorMessage = "";

      if (
        !this.startViewingDate ||
        !this.startViewingTime ||
        !this.endViewingDate ||
        !this.endViewingTime
      ) {
        return;
      }

      const startViewingDateTime = new Date(
        `${this.startViewingDate}T${this.startViewingTime || "00:00"}`,
      );
      const endViewingDateTime = new Date(
        `${this.endViewingDate}T${this.endViewingTime || "00:00"}`,
      );

      if (endViewingDateTime < startViewingDateTime) {
        this.viewingPeriodErrorMessage = this.$t(
          "newPackage.validation.dateAndTime.endDateError",
        ) as string;
        return;
      }

      for (const campaign of this.newCampaign) {
        if (campaign.isSameAsViewingPeriod) {
          campaign.errorMessage = "";
          continue;
        }

        if (
          campaign.startInterviewDate ||
          campaign.startInterviewTime ||
          campaign.endInterviewDate ||
          campaign.endInterviewTime
        ) {
          if (
            !campaign.startInterviewDate ||
            !campaign.startInterviewTime ||
            !campaign.endInterviewDate ||
            !campaign.endInterviewTime
          ) {
            campaign.errorMessage = this.$t(
              "newPackage.validation.required",
            ) as string;
            return;
          }

          const startInterviewDateTime = new Date(
            `${campaign.startInterviewDate}T${
              campaign.startInterviewTime || "00:00"
            }`,
          );
          const endInterviewDateTime = new Date(
            `${campaign.endInterviewDate}T${
              campaign.endInterviewTime || "00:00"
            }`,
          );

          if (endInterviewDateTime < startInterviewDateTime) {
            campaign.errorMessage = this.$t(
              "newPackage.validation.dateAndTime.endDateError",
            ) as string;
            return;
          }

          if (
            startViewingDateTime > startInterviewDateTime ||
            endViewingDateTime < endInterviewDateTime
          ) {
            campaign.errorMessage = this.$t(
              "newPackage.validation.dateAndTime.interviewPeriodError",
            ) as string;
            return;
          }

          campaign.startAt = createIsoUtcDateTime(
            campaign.startInterviewDate,
            campaign.startInterviewTime,
          );
          campaign.endAt = createIsoUtcDateTime(
            campaign.endInterviewDate,
            campaign.endInterviewTime,
          );
        }
        campaign.errorMessage = "";
      }
    },

    onUpdateCampaignField({
      index,
      field,
      value,
    }: {
      index: number;
      field: string;
      value: string | boolean | null;
    }) {
      this.$set(this.newCampaign[index], field, value);
      if (field === "scenarioDocId") {
        const scenario = this.scenarios.find((s) => s.id === value);
        if (scenario) {
          const item = getScenarioDisplayName(scenario, this.agentGroups);
          this.$set(this.newCampaign[index], "name", item.scenarioName);
        }
      }
    },

    onSameAsViewingPeriodChange({
      campaign,
      index,
    }: {
      campaign: NewCampaign;
      index: number;
    }) {
      this.$set(this.newCampaign, index, { ...campaign });
    },

    createNewCampaign() {
      this.newCampaign.push({
        name: "",
        scenarioDocId: "",
        isSameAsViewingPeriod: true,
        startInterviewDate: this.startViewingDate,
        startInterviewTime: this.startViewingTime,
        endInterviewDate: this.endViewingDate,
        endInterviewTime: this.endViewingTime,
        errorMessage: "",
        limits: 1,
        limitMenuOpen: false,
      });
    },

    deleteCampaign(index: number) {
      if (confirm(this.$t("newPackage.campaign.delete") as string)) {
        this.newCampaign.splice(index, 1);
      }
    },

    moveUp(index: number) {
      if (index > 0) {
        const temp = this.newCampaign[index];
        this.$set(this.newCampaign, index, this.newCampaign[index - 1]);
        this.$set(this.newCampaign, index - 1, temp);
      }
    },

    moveDown(index: number) {
      if (index < this.newCampaign.length - 1) {
        const temp = this.newCampaign[index];
        this.$set(this.newCampaign, index, this.newCampaign[index + 1]);
        this.$set(this.newCampaign, index + 1, temp);
      }
    },

    resetValues() {
      this.file = null;
      this.allPackages = [];
      this.allCampaigns = [];
      this.newCampaign = [
        {
          name: "",
          scenarioDocId: "",
          isSameAsViewingPeriod: true,
          startInterviewDate: null,
          startInterviewTime: "00:00",
          endInterviewDate: null,
          endInterviewTime: "23:59",
          limits: 1,
          limitMenuOpen: false,
          errorMessage: "",
          startAt: "",
          endAt: "",
        },
      ];
      this.csvCoupons = [];
      this.couponInputMethod = "csv";
      this.seatCount = 1;
      this.scenarios = [];
      this.packageCode = "";
      this.packageName = "";
      this.duplicatedPackageCodeMessage = "";
      this.CSVInputErrorMessage = "";
      this.viewingPeriodErrorMessage = "";
      this.startViewingDate = dayjs().format("YYYY-MM-DD");
      this.startViewingTime = "00:00";
      this.endViewingDate = dayjs().add(1, "year").format("YYYY-MM-DD");
      this.endViewingTime = "23:59";
    },
  },
});
</script>
